import React, { CSSProperties, useState } from 'react';
import '@/ui/tokens.css';
import './style-gallery.css';
import { getStylesByCategory, PRODUCT_LABELS, ProductKey, StyleDefinition } from '@/design-intelligence/style-definitions';
import { useStyleStore } from '@/design-intelligence/useStyleStore';
import { buildSemanticTokens } from '@/design-intelligence/tokens';

const SECTION_CONFIG: Array<{
  product: ProductKey;
  category: StyleDefinition['category'];
  title: string;
  description: string;
}> = [
  {
    product: 'projectManagerApp',
    category: 'internal-ops',
    title: 'Project Manager App Styles',
    description: 'Internal-ops directions for dashboards, tables, document workflows, and dense daily information work.',
  },
  {
    product: 'checkInPlatform',
    category: 'field-operations',
    title: 'Check-In Platform Styles',
    description: 'Field-operation directions for mobile-first actions, status visibility, and fast real-world task completion.',
  },
];

const EXTRACTED_CONCEPTS = [
  'Structured style taxonomy instead of one-off mood choices',
  'Semantic token grouping for color, spacing, radius, and density',
  'Typography hierarchy chosen to match product context and workload',
  'UX rules tied to context-fit, clarity, and anti-pattern avoidance',
  'A compare-then-lock workflow that turns design preference into reusable project rules',
];

const INTERNAL_SIMPLIFICATIONS = [
  'Ten fixed candidate styles instead of a large multi-domain generator',
  'One internal gallery page instead of external CLI packaging or assistant wrappers',
  'Two product outputs only: Project Manager App and Check-In Platform',
  'Local selection persistence for evaluation, with repo-locked defaults as the baseline source of truth',
  'Token and theme exports trimmed to the needs of future Codex-guided UI work in this repo',
];

function withAlpha(hex: string, alpha: number) {
  const value = hex.replace('#', '');
  const normalized = value.length === 3
    ? value.split('').map((part) => `${part}${part}`).join('')
    : value;
  const parsed = parseInt(normalized, 16);
  const red = (parsed >> 16) & 255;
  const green = (parsed >> 8) & 255;
  const blue = parsed & 255;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function getPreviewMetrics(style: StyleDefinition) {
  if (style.tokens.density === 'high') {
    return { gap: 10, textSize: 12, tableHeight: 34 };
  }
  if (style.tokens.density === 'low') {
    return { gap: 16, textSize: 13, tableHeight: 42 };
  }
  return { gap: 12, textSize: 12, tableHeight: 38 };
}

function getMoodBoardImage(style: StyleDefinition, hint: string, index: number) {
  const seed = encodeURIComponent(`${style.id}-${index}-${hint}`);
  return `https://picsum.photos/seed/${seed}/720/520`;
}

function StyleGalleryPage() {
  const { selectedStyles, activeStyles, setSelectedStyle } = useStyleStore();
  const [showTokens, setShowTokens] = useState(true);

  return (
    <div className="style-gallery">
      <div className="style-gallery__shell">
        <header className="style-gallery__hero">
          <section className="style-gallery__hero-panel">
            <span className="style-gallery__eyebrow">Internal Design Intelligence Layer</span>
            <h1>Style Selection System With Mood Boards</h1>
            <p>
              We extracted the useful design-system thinking from the reference project and rebuilt it here as a
              lighter internal workflow: compare style directions visually, preview semantic tokens, inspect realistic
              UI fragments, choose one direction per product, then lock those decisions into reusable files for future
              product work.
            </p>

            <div className="style-gallery__method-grid">
              <div className="style-gallery__method-card">
                <h2>Extracted Concepts</h2>
                <ul>
                  {EXTRACTED_CONCEPTS.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="style-gallery__method-card">
                <h2>Simplified For Internal Use</h2>
                <ul>
                  {INTERNAL_SIMPLIFICATIONS.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <aside className="style-gallery__hero-side">
            <div>
              <h2>Locked Baseline</h2>
              <p>
                The repo keeps one baseline style per product. This page lets us compare and persist temporary
                selections locally before we intentionally update the locked files.
              </p>
            </div>

            <div className="style-gallery__selection-summary">
              <div className="style-gallery__selection-item">
                <div className="style-gallery__selection-label">{PRODUCT_LABELS.projectManagerApp}</div>
                <strong>{activeStyles.projectManagerApp?.name ?? 'Not selected'}</strong>
                <div className="style-gallery__selection-meta">
                  {activeStyles.projectManagerApp?.description}
                </div>
              </div>
              <div className="style-gallery__selection-item">
                <div className="style-gallery__selection-label">{PRODUCT_LABELS.checkInPlatform}</div>
                <strong>{activeStyles.checkInPlatform?.name ?? 'Not selected'}</strong>
                <div className="style-gallery__selection-meta">
                  {activeStyles.checkInPlatform?.description}
                </div>
              </div>
            </div>
          </aside>
        </header>

        <div className="style-gallery__toolbar">
          <div className="style-gallery__section-header">
            <div>
              <h2>Compare, Apply, Then Lock</h2>
              <p>
                Local selections update immediately for evaluation. When the team decides, we can promote the chosen
                pair into the locked repo files.
              </p>
            </div>
          </div>

          <button
            className="style-gallery__toggle"
            type="button"
            onClick={() => setShowTokens((current) => !current)}
          >
            {showTokens ? 'Hide Token Details' : 'Show Token Details'}
          </button>
        </div>

        {SECTION_CONFIG.map((section) => {
          const styles = getStylesByCategory(section.category);

          return (
            <section className="style-gallery__section" key={section.product}>
              <div className="style-gallery__section-header">
                <div>
                  <h2>{section.title}</h2>
                  <p>{section.description}</p>
                </div>
              </div>

              <div className="style-gallery__grid">
                {styles.map((style) => {
                  const selectedId = selectedStyles[section.product];
                  const isSelected = selectedId === style.id;

                  return (
                    <StyleCard
                      key={style.id}
                      product={section.product}
                      style={style}
                      isSelected={isSelected}
                      showTokens={showTokens}
                      onApply={() => setSelectedStyle(section.product, style.id)}
                    />
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function StyleCard({
  product,
  style,
  isSelected,
  showTokens,
  onApply,
}: {
  product: ProductKey;
  style: StyleDefinition;
  isSelected: boolean;
  showTokens: boolean;
  onApply: () => void;
}) {
  const previewTokens = buildSemanticTokens(style, product);
  const metrics = getPreviewMetrics(style);
  const previewStyle = {
    '--preview-primary': previewTokens.colors.primary,
    '--preview-background': previewTokens.colors.background,
    '--preview-surface': previewTokens.colors.surface,
    '--preview-text': previewTokens.colors.text,
    '--preview-muted': previewTokens.colors.muted,
    '--preview-success': previewTokens.colors.success,
    '--preview-danger': previewTokens.colors.danger,
    '--preview-border': previewTokens.colors.border,
    '--preview-accent-soft': previewTokens.colors.accentSoft,
    '--preview-radius': `${previewTokens.radius.md}px`,
    '--preview-gap': `${metrics.gap}px`,
    '--preview-font': previewTokens.typography.fontFamily,
  } as CSSProperties;

  return (
    <article className={`style-gallery__card${isSelected ? ' style-gallery__card--selected' : ''}`}>
      <div className="style-gallery__card-topline" style={{ background: style.tokens.colors.primary }} />
      <div className="style-gallery__card-body">
        <div className="style-gallery__card-header">
          <div>
            <h3>{style.name}</h3>
            <p className="style-gallery__card-description">{style.description}</p>
          </div>
          {isSelected ? (
            <span
              className="style-gallery__badge"
              style={{
                background: withAlpha(style.tokens.colors.primary, 0.12),
                color: style.tokens.colors.primary,
              }}
            >
              Selected
            </span>
          ) : null}
        </div>

        <div className="style-gallery__meta-block">
          <h4>Extraction Notes</h4>
          <div className="style-gallery__concept-tags">
            {style.extractionNotes.basedOnConcepts.map((concept) => (
              <span className="style-gallery__concept-tag" key={concept}>
                {concept}
              </span>
            ))}
          </div>
          <div className="style-gallery__adapted-for">{style.extractionNotes.adaptedFor}</div>
        </div>

        <div className="style-gallery__mood-grid">
          {style.moodBoard.imageHints.map((hint, index) => (
            <div
              className="style-gallery__mood-item"
              key={hint}
              style={{
                backgroundImage: `linear-gradient(180deg, ${withAlpha(style.tokens.colors.primary, 0.08)}, ${withAlpha(style.tokens.colors.text, 0.2)}), url("${getMoodBoardImage(style, hint, index)}")`,
              }}
            >
              <span className="style-gallery__mood-label">{hint}</span>
            </div>
          ))}
        </div>

        {showTokens ? (
          <div className="style-gallery__meta-block">
            <h4>Token Preview</h4>
            <div className="style-gallery__token-grid">
              {[
                ['Primary', style.tokens.colors.primary],
                ['Surface', style.tokens.colors.surface],
                ['Background', style.tokens.colors.background],
                ['Text', style.tokens.colors.text],
              ].map(([label, color]) => (
                <div className="style-gallery__token-chip" key={label}>
                  <div className="style-gallery__token-swatch" style={{ background: color as string }} />
                  <div className="style-gallery__token-label">
                    <strong>{label}</strong>
                    <br />
                    {color}
                  </div>
                </div>
              ))}
            </div>
            <div className="style-gallery__token-summary">
              <span className="style-gallery__token-pill">Density: {style.tokens.density}</span>
              <span className="style-gallery__token-pill">Radius: {style.tokens.radius}px</span>
              <span className="style-gallery__token-pill">Spacing Base: {style.tokens.spacing}px</span>
            </div>
          </div>
        ) : null}

        <div className="style-gallery__preview" style={previewStyle}>
          <div className="style-gallery__preview-header">
            <h4>Mini UI Preview</h4>
            <span>{style.category === 'internal-ops' ? 'Internal Ops' : 'Field Flow'}</span>
          </div>

          {style.category === 'internal-ops' ? (
            <ProjectManagerPreview styleDefinition={style} />
          ) : (
            <CheckInPreview styleDefinition={style} />
          )}
        </div>

        <div className="style-gallery__actions">
          <div className="style-gallery__keywords">
            Keywords: {style.moodBoard.keywords.join(' · ')}
          </div>
          <button
            className="style-gallery__apply"
            type="button"
            disabled={isSelected}
            onClick={onApply}
            style={{
              background: isSelected
                ? `linear-gradient(135deg, ${style.tokens.colors.success}, ${style.tokens.colors.primary})`
                : `linear-gradient(135deg, ${style.tokens.colors.primary}, ${withAlpha(style.tokens.colors.primary, 0.82)})`,
              boxShadow: `0 12px 28px ${withAlpha(style.tokens.colors.primary, 0.28)}`,
            }}
          >
            {isSelected ? 'Applied To Product' : 'Apply Style'}
          </button>
        </div>
      </div>
    </article>
  );
}

function ProjectManagerPreview({ styleDefinition }: { styleDefinition: StyleDefinition }) {
  const metrics = getPreviewMetrics(styleDefinition);
  const rowHeight = `${metrics.tableHeight}px`;

  return (
    <div className="style-gallery__preview-frame">
      <div className="style-gallery__preview-filter">
        <span className="style-gallery__preview-pill">Open Tasks</span>
        <span className="style-gallery__preview-pill">March</span>
        <span className="style-gallery__preview-pill">Priority</span>
        <button className="style-gallery__preview-filter-button" type="button">
          Apply Filters
        </button>
      </div>

      <div className="style-gallery__preview-table">
        <div className="style-gallery__preview-table-head">
          <span>Project</span>
          <span>Owner</span>
          <span>Status</span>
        </div>
        {[
          ['Warehouse Upgrade', 'JP', 'Review'],
          ['Tender Pack', 'Finance', 'Draft'],
          ['RFI Tracker', 'Admin', 'Live'],
        ].map(([name, owner, status]) => (
          <div className="style-gallery__preview-row" key={name} style={{ minHeight: rowHeight }}>
            <strong style={{ fontSize: metrics.textSize + 1 }}>{name}</strong>
            <span style={{ color: 'var(--preview-muted)', fontSize: metrics.textSize }}>{owner}</span>
            <span
              className="style-gallery__preview-pill"
              style={{
                justifySelf: 'start',
                minHeight: 28,
                background: status === 'Live' ? 'var(--preview-success)' : 'var(--preview-accent-soft)',
                color: status === 'Live' ? '#fff' : 'var(--preview-primary)',
              }}
            >
              {status}
            </span>
          </div>
        ))}
      </div>

      <div className="style-gallery__preview-card">
        <strong>Weekly Ops Snapshot</strong>
        <p>11 invoices need review, 3 quotations are ready to send, and 4 deadlines need owner confirmation today.</p>
        <button className="style-gallery__preview-button" type="button">
          Open Work Queue
        </button>
      </div>
    </div>
  );
}

function CheckInPreview({ styleDefinition }: { styleDefinition: StyleDefinition }) {
  const isUrgent = styleDefinition.id === 'emergency-priority';

  return (
    <div className="style-gallery__preview-frame">
      <button className="style-gallery__preview-large-button" type="button">
        {isUrgent ? 'Acknowledge Incident' : 'Start Site Check-In'}
      </button>

      <div className="style-gallery__preview-status">
        <strong>{isUrgent ? 'Priority Status' : 'Current Site Status'}</strong>
        <p>
          {isUrgent
            ? 'Two urgent flags require escalation and one field team is awaiting confirmation.'
            : 'Crew on site, supervisor verified, and today’s attendance sync is healthy.'}
        </p>
        <div className="style-gallery__preview-status-bar">
          <span className="style-gallery__preview-status-segment style-gallery__preview-status-segment--live" />
          <span className="style-gallery__preview-status-segment" />
          <span
            className="style-gallery__preview-status-segment"
            style={{ background: isUrgent ? 'var(--preview-danger)' : 'var(--preview-accent-soft)' }}
          />
        </div>
      </div>

      <div className="style-gallery__preview-input">
        <label>Worker ID</label>
        <div className="style-gallery__preview-input-field">
          {styleDefinition.id === 'kiosk-mode' ? 'Tap badge or enter PIN' : 'Scan badge / enter employee number'}
        </div>
      </div>

      <div className="style-gallery__preview-confirmation">
        <div>
          <strong>{isUrgent ? 'Escalation Logged' : 'Check-In Confirmed'}</strong>
          <p>{isUrgent ? 'Supervisor notified and response timer running.' : 'Timestamp saved and attendance record synced.'}</p>
        </div>
        <span className="style-gallery__preview-confirmation-mark">{isUrgent ? '!' : '✓'}</span>
      </div>
    </div>
  );
}

export default StyleGalleryPage;
